/**
 * Detail view for one email/courier order, opened from EmailOrderCard.
 * Deliberately order-level only (customer, dropoff, items, payment, driver) — no
 * raw email / parsed-fields view. That deeper drill-down (the actual email
 * content, why it's still under review, etc.) stays on the Partner Orders page;
 * this is the quick "what is this order" a restaurant needs from the main
 * Orders screen without leaving it.
 *
 * The one action here — Mark Picked Up — only ever appears for non-delivery
 * orders (dine-in/pickup/takeout), gated on order.orderKind !== 'DELIVERY'.
 * A delivery order's lifecycle is First Delivery's alone; nothing here can
 * touch it. Even an order that's still just a parsed email with no
 * CourierOrder row yet (order.status is the email's own status — RECEIVED/
 * PARSED/NEEDS_REVIEW) can use this — see EmailOrderCard's header comment for
 * how the id tells the two cases apart. On an explicitly-unpaid order the
 * button reads "Mark Paid & Picked Up" and flips isPaid too in the same call
 * — the pay-at-pickup case, where paying and picking up are one moment for
 * the customer, so recording them as two separate restaurant actions was
 * just friction.
 *
 * Deliberately does NOT show classify.js's dispatch-confidence blocker
 * (e.g. "needs a second agreeing signal") — that question is about whether
 * it's safe to AUTO-dispatch, which has nothing to do with a restaurant
 * manually confirming a pickup here.
 */
import React from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, IconButton,
} from '@mui/material'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { COURIER_STATUS_CONFIG } from './EmailOrderCard'
import EmailSourceChip from './EmailSourceChip'
import { ops, FS, RADIUS, tabular, labelSx, focusRing, btn, dialogPaperSx, backdropSx } from './opsTokens'
import {
  StatusChip, Tag, FulfilmentTag, InitialsAvatar, VehicleIcon, ProviderMark, InfoGrid, InfoField,
  EtaTile, SectionTitle, ItemRow, ModLine, SumRow, TotalRow, DriverCard, DriverIdentity, ZoomablePhoto
} from './OpsUi'

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`)
const when = (s) => (s ? new Date(s).toLocaleString() : '—')
const minsSince = (s) => (s ? Math.round((Date.now() - new Date(s).getTime()) / 60000) : null)

const CLOSED_STATUSES = new Set(['PICKED_UP', 'DELIVERED', 'CANCELLED', 'FAILED'])

const eta = (s) => {
  if (!s) return null
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

const TRANSPORT_LABEL = { car: 'Car', bike: 'Bike', bicycle: 'Bicycle', scooter: 'Scooter' }

const EmailOrderDetail = ({ order, onClose, onMarkPickedUp, markingPickedUp }) => {
  /* Proof of delivery enlarges over the order (OpsUi ZoomablePhoto). The
     thumbnail is cropped square to keep the dialog a readable length, so the
     part that settles a "we never got it" call — the house number, which door —
     is usually the part cropped away; a new browser tab would lose the order. */
  if (!order) return null
  const config = COURIER_STATUS_CONFIG[order.status] || { label: order.status, tone: 'neutral' }
  const canMarkPickedUp = !!order.orderKind && order.orderKind !== 'DELIVERY' && !CLOSED_STATUSES.has(order.status)
  const pickupEta = eta(order.fdPickupEta)
  const dropoffEta = eta(order.fdDropoffEta)

  return (
    <>
    <Dialog
      open={!!order}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { ...dialogPaperSx, width: 'calc(100% - 32px)', maxWidth: 720, m: 2, maxHeight: 'calc(100% - 32px)' } }}
      BackdropProps={{ sx: backdropSx }}
    >
      <DialogTitle component="div" sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: `1px solid ${ops.border}` }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
          <Box minWidth={0}>
            <Typography component="h2" sx={{ fontSize: FS.xl, fontWeight: 600, color: ops.text, lineHeight: 1.3, ...tabular }}>
              #{order.externalOrderNumber || order._id}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
              <StatusChip toneName={config.tone} label={config.label} />
              <EmailSourceChip provider={order.merchantProvider} label="Email order" />
              {order.orderKind && (
                <FulfilmentTag kind={order.orderKind === 'DELIVERY' ? 'delivery' : 'pickup'}>
                  {order.orderKind === 'DELIVERY' ? 'Delivery' : order.orderKind}
                </FulfilmentTag>
              )}
            </Stack>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ width: 36, height: 36, borderRadius: RADIUS.button, color: ops.muted, flexShrink: 0, '&:hover': { bgcolor: ops.sunken, color: ops.text }, ...focusRing }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, py: '20px !important' }}>
        <Stack gap={2.5}>
          <InfoGrid>
            <InfoField label="Customer">
              <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                <InitialsAvatar name={order.customerName} size={28} />
                <Box minWidth={0}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.customerName || '—'}</Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: FS.sm, fontWeight: 400, color: ops.muted, ...tabular }}>
                    {order.customerPhone && <PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                    {order.customerPhone || 'no phone'}
                  </Box>
                </Box>
              </Box>
            </InfoField>

            <InfoField label="Restaurant" icon={<StorefrontOutlinedIcon />}>{order.merchantName || '—'}</InfoField>

            <InfoField label="Dropoff address" icon={<PlaceOutlinedIcon />}>
              {order.dropoffAddress || '—'}
            </InfoField>

            <InfoField label={order.orderKind === 'DELIVERY' ? 'Due' : 'Estimated ready time'} icon={<ScheduleOutlinedIcon />}>
              <Box component="span" sx={tabular}>{when(order.promiseTime)}</Box>
            </InfoField>
          </InfoGrid>

          {/* First Delivery only ever touches a DELIVERY order — showing this for a
              pickup/dine-in one would claim a dispatch that never happens. */}
          {order.orderKind === 'DELIVERY' && (
            <DriverCard label="Delivery status">
              <DriverIdentity
                avatar={order.fdDriverName
                  ? <InitialsAvatar name={order.fdDriverName} size={44} />
                  : (
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: ops.sunken, color: ops.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                  )}
                name={order.fdDriverName || (order.fdOrderId ? 'Driver not yet assigned' : 'Not dispatched yet')}
                tags={
                  <>
                    {order.fdDriverTransport && (
                      <Tag outlined icon={<VehicleIcon transport={order.fdDriverTransport} />}>
                        {TRANSPORT_LABEL[order.fdDriverTransport] || order.fdDriverTransport}
                      </Tag>
                    )}
                    {order.fdDspName && (
                      <Tag icon={<ProviderMark name={order.fdDspName} size={14} />}>{order.fdDspName}</Tag>
                    )}
                  </>
                }
                sub={
                  <>
                    {order.fdDriverPhone ? <><PhoneOutlinedIcon sx={{ fontSize: 14 }} />{`${order.fdDriverPhone} · `}</> : ''}
                    {'last update '}{order.lastFdEventAt ? `${minsSince(order.lastFdEventAt)}m ago` : 'none yet'}
                  </>
                }
              />

              {(pickupEta || dropoffEta) && (
                <Stack direction="row" gap={1}>
                  {pickupEta && <EtaTile label="PICKUP ETA" value={pickupEta} />}
                  {dropoffEta && <EtaTile label="DROPOFF ETA" value={dropoffEta} />}
                </Stack>
              )}

              {/* Tracking pages are the provider's own and refuse to be framed,
                  so these are the one thing here that must open in a new tab. */}
              {(order.fdTrackerLink || order.fdDspTrackerLink) && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {order.fdTrackerLink && (
                    <Button endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      href={order.fdTrackerLink} target="_blank" rel="noopener noreferrer"
                      sx={{ ...btn.secondary, flex: 1, minWidth: 120 }}>
                      Tracker 1
                    </Button>
                  )}
                  {order.fdDspTrackerLink && (
                    <Button endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      href={order.fdDspTrackerLink} target="_blank" rel="noopener noreferrer"
                      sx={{ ...btn.secondary, flex: 1, minWidth: 120 }}>
                      Tracker 2
                    </Button>
                  )}
                </Stack>
              )}

              {/* Say why there is no photo rather than showing nothing. Proof of
                  delivery only exists once the driver has dropped off, and even
                  then the provider does not always capture one — in production
                  29 of 55 delivered orders have a photo. A silent gap here reads
                  as a broken feature. */}
              {order.fdDropoffPhoto ? (
                <Box>
                  <Typography component="div" sx={{ ...labelSx, mb: 0.75 }}>
                    PROOF OF DELIVERY
                  </Typography>
                  <ZoomablePhoto src={order.fdDropoffPhoto} alt="Proof of delivery" title="PROOF OF DELIVERY" width={160} />
                </Box>
              ) : order.status === 'DELIVERED' ? (
                <Typography sx={{ fontSize: FS.sm, color: ops.muted }}>
                  No delivery photo was provided for this order
                </Typography>
              ) : order.fdOrderId ? (
                <Typography sx={{ fontSize: FS.sm, color: ops.muted }}>
                  Delivery photo appears once the driver drops off
                </Typography>
              ) : null}
            </DriverCard>
          )}

          <Box>
            <SectionTitle>Items</SectionTitle>
            <Box sx={{ borderTop: `1px solid ${ops.border}` }}>
              {(order.items || []).map((it, idx, all) => {
                let mods = []
                try { mods = it.modifiers ? JSON.parse(it.modifiers) : [] } catch { mods = [] }
                return (
                  <ItemRow
                    key={it._id}
                    qty={`${it.quantity}×`}
                    last={idx === all.length - 1}
                    price={<Typography sx={{ fontSize: FS.md, fontWeight: 600, color: ops.text, ...tabular }}>{money(it.unitPrice)}</Typography>}
                  >
                    <Typography sx={{ fontSize: FS.md, fontWeight: 500, color: ops.text }}>{it.title}</Typography>
                    {mods.map((m, j) => (
                      <ModLine key={j} indent={1}>+ {m.title}{m.price != null ? ` (${money(m.price)})` : ''}</ModLine>
                    ))}
                  </ItemRow>
                )
              })}
            </Box>
          </Box>

          <Box p={2} sx={{ bgcolor: ops.subtle, borderRadius: RADIUS.tile, border: `1px solid ${ops.border}` }}>
            {/* null is genuinely unknown (no payment-status line on the receipt at
                all) — must read differently from an explicit Unpaid, never a
                quiet default to either. */}
            <SectionTitle
              right={
                <StatusChip
                  toneName={order.isPaid == null ? 'slate' : order.isPaid ? 'emerald' : 'rose'}
                  label={
                    order.isPaid == null
                      ? 'Status unknown'
                      : `${order.isPaid ? 'Paid' : 'Unpaid'}${order.paymentMethod ? ` · ${order.paymentMethod}` : ''}`
                  }
                />
              }
            >
              Payment
            </SectionTitle>
            {[
              ['Subtotal', order.subtotal], ['Delivery fee', order.deliveryFee], ['Processing fee', order.processingFee],
              ['Tax', order.tax], ['Tip (driver)', order.tip],
            ].map(([l, v]) => <SumRow key={l} label={l} value={money(v)} />)}
            <TotalRow label="Total" value={money(order.total)} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: `1px solid ${ops.border}`, gap: 1 }}>
        <Button onClick={onClose} sx={{ ...btn.secondary, px: 2 }}>Close</Button>
        <Box sx={{ flex: 1 }} />
        {canMarkPickedUp && (
          <Button
            variant="contained"
            disableElevation
            disabled={markingPickedUp}
            onClick={() => onMarkPickedUp && onMarkPickedUp(order, order.isPaid === false)}
            sx={{ ...btn.primary, px: 2 }}
          >
            {markingPickedUp ? 'Marking…' : (order.isPaid === false ? 'Mark Paid & Picked Up' : 'Mark Picked Up')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  )
}

export default EmailOrderDetail
